import React, { useState } from 'react';
import { ProjectProvider, useProject } from './context/ProjectContext';
import Sidebar from './components/layout/Sidebar';
import SubjectDualForm from './features/create/SubjectDualForm';
import FourPoseAvatarUploader from './features/create/FourPoseAvatarUploader';
import VoicePicker from './features/create/VoicePicker';
import BackgroundPicker from './features/create/BackgroundPicker';
import ScriptEditorSection from './features/script/ScriptEditorSection';
import StudioRightPanel from './features/render/StudioRightPanel';
import Toast from './components/ui/Toast';
import Modal from './components/ui/Modal';
import Button from './components/ui/Button';
import RechargeCreditModal from './features/billing/RechargeCreditModal';
import './App.css';

function MainCreatorStudio() {
  const {
    credits,
    project,
    updateProject,
    catalogs,
    savedProjects,
    isGeneratingScript,
    isGeneratingAudio,
    isStartingRender,
    isCancellingJob,
    currentJob,
    audioUrl,
    isPlayingAudio,
    playingVoiceId,
    handlePreviewVoice,
    handleGenerateScript,
    handleGenerateScriptAudio,
    handleToggleAudio,
    handleUploadAsset,
    handleRemoveAsset,
    handleUploadStaticAvatar,
    handleUploadPose,
    handleRemovePose,
    handleGenerateAvatarPoses,
    isGeneratingPoses,
    handleStartRender,
    handleCancelRender,
    handleSelectProject,
    handleDeleteProject,
    handleNewProject,
    handleAddCredits,
    toast
  } = useProject();

  const [isCreditPricingModalOpen, setIsCreditPricingModalOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden', backgroundColor: 'var(--bg-app)' }}>
      {/* 1. LEFT COLUMN: SIDEBAR */}
      <Sidebar
        credits={credits}
        projects={savedProjects}
        activeProjectId={project.id}
        onSelectProject={handleSelectProject}
        onNewProject={handleNewProject}
        onDeleteProject={handleDeleteProject}
        onAddCredits={handleAddCredits}
        onOpenRecharge={() => setIsRechargeModalOpen(true)}
      />

      {/* 2. CENTER COLUMN: MAIN WORKSPACE */}
      <main
        style={{
          flex: 1,
          padding: '20px 24px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {/* Step 1 & 2 Header Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              BƯỚC 1 & 2: THIẾT LẬP DỮ LIỆU
            </span>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '-0.02em', marginTop: '1px' }}>
              Thiết Lập Video & Kịch Bản So Sánh
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <span style={{ fontSize: '11px', fontWeight: 700, background: '#FFF7ED', color: '#EA580C', padding: '4px 10px', borderRadius: 'var(--radius-full)', border: '1px solid rgba(234, 88, 12, 0.2)' }}>
              Render MP4 thật
            </span>
          </div>
        </div>

        {/* 2-Column Grid Layout in Main Area */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: '16px', alignItems: 'start' }}>
          {/* Left Sub-Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 1. Subject A vs B Dual Form */}
            <div className="studio-card" style={{ padding: '18px' }}>
              <SubjectDualForm
                project={project}
                onChangeProject={updateProject}
                onUploadAsset={handleUploadAsset}
                onRemoveAsset={handleRemoveAsset}
              />
            </div>

            {/* 2. Mascot / Avatar Uploader */}
            <div className="studio-card" style={{ padding: '18px' }}>
              <FourPoseAvatarUploader
                mode={project.avatarMode || 'static'}
                onModeChange={(mode) => updateProject({ avatarMode: mode })}
                staticAvatarUrl={project.avatarUrl}
                onUploadStaticAvatar={handleUploadStaticAvatar}
                onRemoveStaticAvatar={() => updateProject({ avatarUrl: '' })}
                poses={project.poses || {}}
                onUploadPose={handleUploadPose}
                onRemovePose={handleRemovePose}
                onGeneratePoses={handleGenerateAvatarPoses}
                isGeneratingPoses={isGeneratingPoses}
              />
            </div>
          </div>

          {/* Right Sub-Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* 3. Voice & Background Picker */}
            <div className="studio-card" style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <VoicePicker
                voices={catalogs.voices || []}
                selectedVoiceId={project.voiceId}
                onSelectVoice={(voiceId) => updateProject({ voiceId })}
                onPreviewVoice={handlePreviewVoice}
                playingVoiceId={playingVoiceId}
              />

              <div style={{ height: '1px', background: 'var(--border-color)' }} />

              <BackgroundPicker
                backgrounds={catalogs.backgrounds || []}
                selectedBackgroundId={project.backgroundId}
                onSelectBackground={(bgId) => updateProject({ backgroundId: bgId })}
              />
            </div>

            {/* 4. Script Video Section */}
            <div className="studio-card" style={{ padding: '18px' }}>
              <ScriptEditorSection
                script={project.script || {}}
                onChangeScript={(updated) => updateProject({ script: updated })}
                onGenerateScript={handleGenerateScript}
                onGenerateAudio={handleGenerateScriptAudio}
                isGeneratingScript={isGeneratingScript}
                isGeneratingAudio={isGeneratingAudio}
              />
            </div>
          </div>
        </div>
      </main>

      {/* 3. RIGHT COLUMN: 9:16 PHONE PREVIEW & RENDER DASHBOARD (Enlarged 450px for Clear Review) */}
      <aside
        style={{
          width: '450px',
          minWidth: '450px',
          height: '100vh',
          padding: '20px 18px',
          borderLeft: '1px solid var(--border-color)',
          backgroundColor: '#FFFFFF',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <StudioRightPanel
          project={project}
          catalogs={catalogs}
          currentJob={currentJob}
          onStartRender={handleStartRender}
          onCancelRender={handleCancelRender}
          isStartingRender={isStartingRender}
          isCancellingJob={isCancellingJob}
          audioUrl={audioUrl}
          isPlayingAudio={isPlayingAudio}
          onToggleAudio={handleToggleAudio}
          onOpenCreditModal={() => setIsCreditPricingModalOpen(true)}
        />
      </aside>

      {/* Credit Pricing Info Modal */}
      <Modal
        isOpen={isCreditPricingModalOpen}
        onClose={() => setIsCreditPricingModalOpen(false)}
        title="Bảng Giá & Định Mức Credit"
        maxWidth="500px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px' }}>
          <p style={{ color: 'var(--text-muted)' }}>
            Hệ thống tính phí theo lượt render thực tế và dịch vụ AI máy chủ:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
              <span>Render Video 9:16 1080p (30s)</span>
              <strong style={{ color: 'var(--primary)' }}>10 credit</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
              <span>Lồng tiếng AI TTS đa ngữ điệu</span>
              <strong style={{ color: 'var(--primary)' }}>2 credit</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: '8px' }}>
              <span>Tự động sinh kịch bản AI</span>
              <strong style={{ color: '#10B981' }}>Miễn phí</strong>
            </div>
          </div>

          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setIsCreditPricingModalOpen(false);
              setIsRechargeModalOpen(true);
            }}
            style={{ width: '100%', marginTop: '6px' }}
          >
            Nạp Thêm Credit Ngay
          </Button>
        </div>
      </Modal>

      {/* Recharge Credit Modal */}
      <RechargeCreditModal
        isOpen={isRechargeModalOpen}
        onClose={() => setIsRechargeModalOpen(false)}
        currentCredits={credits}
        onAddCredits={handleAddCredits}
      />

      {/* Global Toast Notification */}
      <Toast
        visible={toast.visible}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
}

export default function App() {
  return (
    <ProjectProvider>
      <MainCreatorStudio />
    </ProjectProvider>
  );
}
